import { isNotNativeOnlyAd } from '@utils/ads'
import { FunctionComponent } from 'react'
import styled, { css } from 'styled-components'
import useRenderer from '@hooks/useRenderer'
import {
  MultilineArticleContent,
  MultilineArticleGenericContent,
  MultilineArticleGenericMainContent,
  MultilineArticleGenericSubcontent,
  MultilineArticleWithoutArticleContent,
  MultilineArticleSidebarContent,
} from '@widgets/Multiline/types'
import { desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'
import ArticleContentContext from '@contexts/articleContent'
import StoryTellingContentContext from '@contexts/storyTellingContent'
import useIsInRecipeContent from '@hooks/useIsInRecipeContent'

interface ArticleMultilineProps {
  children: MultilineArticleContent
}

interface StyledArticleMultilineProps {
  hasSidebar: boolean
  isLotto: boolean
}

const StyledArticleContent = styled.div`
  display: grid;
  grid-gap: 16px;
  box-sizing: border-box;
  ${desktopCSS(css`
    padding: 0 40px;
  `)}

  ${tabletCSS(css`
    padding: 0 72px;
  `)}

  ${mobileCSS(css`
    padding: 0 12px;
  `)}
`

const StyledArticleSidebar = styled.div`
  display: grid;
  grid-gap: 20px;
  align-content: flex-start;
  justify-content: flex-end;
  box-sizing: border-box;

  ${desktopCSS(css`
    grid-template-columns: minmax(0, 1fr);
    padding-right: 12px;
  `)}

  ${tabletCSS(css`
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 40px 72px 0 72px;
  `)} 

  ${mobileCSS(css`
    grid-template-columns: minmax(0, 1fr);
    padding: 20px 12px 0 12px;
  `)}
`

const StyledArticleMultiline = styled.div<StyledArticleMultilineProps>`
  ${({ hasSidebar, isLotto }) => css`
    grid-column: span 6;
    display: grid;
    grid-row-gap: 20px;
    align-items: start;

    ${isLotto &&
    css`
      ${desktopCSS(css`
        grid-template-columns: minmax(0, 1fr);
      `)}

      ${tabletCSS(css`
        grid-template-columns: minmax(0, 1fr);
      `)}

      ${mobileCSS(css`
        grid-template-columns: minmax(0, 1fr);
      `)}
    `}

    ${!isLotto &&
    hasSidebar &&
    css`
      ${desktopCSS(css`
        grid-template-columns: 656px 312px;
        justify-content: space-between;
      `)}

      ${tabletCSS(css`
        grid-template-columns: minmax(0, 1fr);
      `)}

      ${mobileCSS(css`
        grid-template-columns: minmax(0, 1fr);
      `)}
    `}

    ${!isLotto &&
    !hasSidebar &&
    css`
      ${desktopCSS(css`
        grid-template-columns: 656px;
        justify-content: space-around;
      `)}

      ${tabletCSS(css`
        grid-template-columns: minmax(0, 1fr);
      `)}

      ${mobileCSS(css`
        grid-template-columns: minmax(0, 1fr);
      `)}
    `}

    grid-template-columns: repeat(6, minmax(0, 1fr));
  `}
`

const flattenArticleMainContent = (
  articleContent: MultilineArticleGenericMainContent
) => {
  return articleContent.content.reduce((acc, innerWidget) => {
    if (innerWidget.kind.join('.') === 'layout.column') {
      return [...acc, ...innerWidget.content]
    }
    return [...acc, innerWidget]
  }, [] as Record<string, unknown>[])
}

const flattenArticleSubcontent = (
  articleSubcontent: MultilineArticleGenericSubcontent
) =>
  articleSubcontent.content.reduce((acc, widget) => {
    if (widget.kind.includes('multi-line')) {
      widget.content
        .filter((innerWidget) => isNotNativeOnlyAd(innerWidget))
        .forEach((innerWidget) => {
          if (
            ![
              'widget.recommendation.articles',
              'widget.recommendation.videos',
            ].includes(innerWidget.kind.join('.'))
          ) {
            acc.push(innerWidget)
          }
        })
    } else {
      if (isNotNativeOnlyAd(widget)) {
        acc.push(widget)
      }
    }

    return acc
  }, [] as Record<string, unknown>[])

const flattenArticleContent = (
  layoutItem: MultilineArticleGenericContent
): Record<string, unknown>[] => {
  return [
    ...flattenArticleMainContent(layoutItem.content[0]),
    ...(layoutItem.content[1]
      ? flattenArticleSubcontent(layoutItem.content[1])
      : []),
  ]
}

const pullUpUsefulWidget = (acc: any[], widget: any) => {
  if (!widget) {
    return acc
  }
  if (widget.kind[0] === 'layout') {
    return widget.content.reduce(pullUpUsefulWidget, acc)
  }
  acc.push(widget)
  return acc
}

const flattenArticleSidebar = (
  layoutItem: MultilineArticleSidebarContent
): Record<string, unknown>[] =>
  pullUpUsefulWidget([], layoutItem).filter((widget: any) =>
    isNotNativeOnlyAd(widget)
  )

const isWithoutArticleContent = (
  children: any
): children is MultilineArticleWithoutArticleContent =>
  Array.isArray(children[0]?.content) &&
  !children[0].content[0].kind.includes('article-content')

const ArticleMultiline: FunctionComponent<ArticleMultilineProps> = ({
  children,
}) => {
  const Renderer = useRenderer()
  const isInRecipe = useIsInRecipeContent()

  if (isWithoutArticleContent(children)) {
    return <Renderer>{children[0].content}</Renderer>
  }

  const isStorytelling = children.length === 1

  const isLotto = children[0]?.content[0]?.content.some((widget) =>
    widget.kind.includes('lotto')
  )

  const articleContent = (
    <Renderer>{flattenArticleContent(children[0])}</Renderer>
  )

  const sidebarContent = !isStorytelling ? (
    <Renderer>{flattenArticleSidebar(children[1])}</Renderer>
  ) : null

  return (
    <StyledArticleMultiline
      hasSidebar={!isStorytelling && !isInRecipe}
      isLotto={isLotto}>
      <ArticleContentContext.Provider value={true}>
        <StoryTellingContentContext.Provider value={isStorytelling}>
          <StyledArticleContent>{articleContent}</StyledArticleContent>
        </StoryTellingContentContext.Provider>
      </ArticleContentContext.Provider>
      {!!sidebarContent && (
        <StyledArticleSidebar>{sidebarContent}</StyledArticleSidebar>
      )}
    </StyledArticleMultiline>
  )
}

export default ArticleMultiline
